import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminConfirmDialog,
  AdminField,
  AdminFilterBar,
  AdminInput,
  AdminModal,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminTabPanel,
  AdminTabs,
  AdminToastRegion,
  type AdminToast,
} from './AdminUi'
import { homepageV3 } from '../config/homepageV3'
import ProductImageFramingEditor from './ProductImageFramingEditor'
import {
  DEFAULT_PRODUCT_IMAGE_FRAMING,
  PRODUCT_IMAGE_FRAMING_LIMITS,
  defaultOrderProductFraming,
  defaultProductPresentation,
  normalizeHexColor,
  normalizeStoredProductImageFraming,
  type ProductFraming,
  type ProductImageFraming,
  type ProductPresentation,
} from '../../shared/productPresentation'
import { normalizeProductName, PRODUCT_NAME_MAX_LENGTH } from '../../shared/productName'
import { normalizeProductDescription, PRODUCT_DESCRIPTION_MAX_LENGTH } from '../../shared/productDescription'
import {
  FLAVOUR_DETAIL_LIMITS,
  normalizeApproxWeightG,
  normalizeWhatsInside,
} from '../../shared/flavourDetail'
import {
  CONTROLLED_ALLERGENS,
  isControlledAllergen,
  type ControlledAllergen,
} from '../../shared/allergens'
import { PRODUCT_FAMILIES, type ProductFamily } from '../../shared/productFamily'
import '../styles/admin-content-media.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type HomepageFields = {
  heroHeadingLineOne?: string
  heroHeadingLineTwo?: string
  heroSupportingCopy?: string
  weeklyMenuSupportingCopy?: string
}

type AboutPresentation = {
  stuffedDesktopScale: number
  stuffedDesktopOffsetY: number
}

type MediaItem = {
  id: string
  originalFilename: string
  mimeType: 'image/png' | 'image/jpeg' | 'image/webp'
  byteSize: number
  width: number
  height: number
  status: 'available' | 'archived'
  createdAt: string
  updatedAt: string
  archivedAt: string | null
  usage: { publishedProducts: string[]; draftProducts: string[] }
  previewUrl: string
}

type ProductGalleryItem = {
  mediaId: string
  altText: string
  framing: ProductImageFraming
}

type ProductAssignment = {
  productId: string
  publishedMediaId: string | null
  publishedAltText: string
  draftMediaId: string | null
  draftAltText: string
  publishedFraming: ProductImageFraming
  draftFraming: ProductImageFraming
  publishedImageScale: number
  publishedImageOffsetX: number
  publishedImageOffsetY: number
  publishedHoverBackground: string
  draftImageScale: number
  draftImageOffsetX: number
  draftImageOffsetY: number
  draftHoverBackground: string
  publishedOrderImageScale: number
  publishedOrderImageOffsetX: number
  publishedOrderImageOffsetY: number
  draftOrderImageScale: number
  draftOrderImageOffsetX: number
  draftOrderImageOffsetY: number
  publishedGallery: ProductGalleryItem[]
  draftGallery: ProductGalleryItem[]
  updatedAt: string
  publishedAt: string | null
  dirty: boolean
}

type SafetyReviewStatus = 'legacy-current' | 'needs-review' | 'reviewed'

type ProductSafetyRecord = {
  status: SafetyReviewStatus
  reviewedAt: string | null
  reviewedBy: string | null
  sourceReference: string
  published: {
    ingredients: string
    contains: string[]
    mayContain: string[]
    legacyUnrecognised: string[]
  }
  draft: {
    ingredients: string
    contains: ControlledAllergen[]
    mayContain: ControlledAllergen[]
    sourceReference: string
    updatedAt: string
    updatedBy: string | null
  } | null
}

type ProductSafetyEdit = {
  ingredients: string
  contains: ControlledAllergen[]
  mayContain: ControlledAllergen[]
  sourceReference: string
}

type ProductRecord = {
  id: string
  name: string
  description: string
  whatsInside: string[]
  approxWeightG: number | null
  ingredients: string
  allergens: string[]
  mayContainAllergens: string[]
  safety: ProductSafetyRecord | null
  productFamily: ProductFamily
  staticImage: string
  active: boolean
  defaultPresentation: ProductPresentation
  defaultOrderPresentation: ProductFraming
  assignment: ProductAssignment | null
}

type ContentMediaSnapshot = {
  storageConfigured: boolean
  manifestVersion: number
  homepage: {
    published: HomepageFields
    draft: HomepageFields
    updatedAt: string | null
    publishedAt: string | null
    dirty: boolean
  }
  about: {
    presentation: AboutPresentation
    updatedAt: string | null
    limits: {
      minScale: number
      maxScale: number
      minOffsetY: number
      maxOffsetY: number
    }
  }
  media: MediaItem[]
  products: ProductRecord[]
  limits: {
    allowedMimeTypes: string[]
    maxBytes: number
    minDimension: number
    maxDimension: number
  }
}

type AssignmentEdit = {
  mediaId: string
  altText: string
  gallery: ProductGalleryItem[]
  framing: ProductImageFraming
  hoverBackground: string
}
type ContentTab = 'homepage' | 'about' | 'media' | 'products'
type MediaFilter = 'all' | 'available' | 'archived'

const HOMEPAGE_DEFAULTS: Required<HomepageFields> = {
  heroHeadingLineOne: homepageV3.top.headingLineOne,
  heroHeadingLineTwo: homepageV3.top.headingLineTwo,
  heroSupportingCopy: homepageV3.top.supportingCopy,
  weeklyMenuSupportingCopy: homepageV3.weeklyMenu.supportingCopy,
}

const DATE_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  dateStyle: 'medium',
  timeStyle: 'short',
})

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function resolveHomepage(fields: HomepageFields) {
  return { ...HOMEPAGE_DEFAULTS, ...fields }
}

function bytesLabel(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

function timestampLabel(value: string | null) {
  if (!value) return 'Not yet'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unknown' : DATE_TIME.format(date)
}

function mediaUsageCount(media: MediaItem) {
  return new Set([...media.usage.publishedProducts, ...media.usage.draftProducts]).size
}

function managedMediaUrl(mediaId: string | null, media: MediaItem[]) {
  if (!mediaId) return null
  return media.find((item) => item.id === mediaId)?.previewUrl ?? null
}

function assignmentDraft(product: ProductRecord): AssignmentEdit {
  const defaults = product.defaultPresentation
  return {
    mediaId: product.assignment?.draftMediaId ?? '',
    altText: product.assignment?.draftAltText || product.assignment?.publishedAltText || product.name,
    gallery: (product.assignment?.draftGallery ?? []).map((item) => ({
      ...item,
      framing: normalizeStoredProductImageFraming(item.framing, DEFAULT_PRODUCT_IMAGE_FRAMING),
    })),
    framing: normalizeStoredProductImageFraming(
      product.assignment?.draftFraming,
      DEFAULT_PRODUCT_IMAGE_FRAMING,
    ),
    hoverBackground: product.assignment?.draftHoverBackground ?? defaults.hoverBackground,
  }
}

function savedDraft(product: ProductRecord): AssignmentEdit {
  return assignmentDraft(product)
}

function publishedImageFraming(product: ProductRecord): ProductImageFraming {
  return normalizeStoredProductImageFraming(
    product.assignment?.publishedFraming,
    DEFAULT_PRODUCT_IMAGE_FRAMING,
  )
}

function sameEdit(left: AssignmentEdit, right: AssignmentEdit) {
  return left.mediaId === right.mediaId
    && left.altText === right.altText
    && JSON.stringify(left.gallery) === JSON.stringify(right.gallery)
    && JSON.stringify(left.framing) === JSON.stringify(right.framing)
    && left.hoverBackground === right.hoverBackground
}

function safetyEditFromProduct(product: ProductRecord): ProductSafetyEdit {
  const source = product.safety?.draft
  if (source) {
    return {
      ingredients: source.ingredients,
      contains: [...source.contains],
      mayContain: [...source.mayContain],
      sourceReference: source.sourceReference,
    }
  }

  return {
    ingredients: product.safety?.published.ingredients ?? product.ingredients,
    contains: (product.safety?.published.contains ?? product.allergens).filter(isControlledAllergen),
    mayContain: (product.safety?.published.mayContain ?? product.mayContainAllergens).filter(isControlledAllergen),
    sourceReference: product.safety?.sourceReference ?? '',
  }
}

function sameSafetyEdit(left: ProductSafetyEdit, right: ProductSafetyEdit) {
  return left.ingredients === right.ingredients
    && JSON.stringify(left.contains) === JSON.stringify(right.contains)
    && JSON.stringify(left.mayContain) === JSON.stringify(right.mayContain)
    && left.sourceReference === right.sourceReference
}

function safetyStatusLabel(status: SafetyReviewStatus) {
  if (status === 'legacy-current') return 'LEGACY CURRENT'
  if (status === 'needs-review') return 'NEEDS REVIEW'
  return 'REVIEWED'
}

function safetyStatusTone(status: SafetyReviewStatus) {
  if (status === 'reviewed') return 'success' as const
  if (status === 'needs-review') return 'warning' as const
  return 'neutral' as const
}

function validImageFraming(framing: ProductImageFraming) {
  return Number.isFinite(framing.focalX)
    && framing.focalX >= PRODUCT_IMAGE_FRAMING_LIMITS.minFocal
    && framing.focalX <= PRODUCT_IMAGE_FRAMING_LIMITS.maxFocal
    && Number.isFinite(framing.focalY)
    && framing.focalY >= PRODUCT_IMAGE_FRAMING_LIMITS.minFocal
    && framing.focalY <= PRODUCT_IMAGE_FRAMING_LIMITS.maxFocal
    && Number.isFinite(framing.zoom)
    && framing.zoom >= PRODUCT_IMAGE_FRAMING_LIMITS.minZoom
    && framing.zoom <= PRODUCT_IMAGE_FRAMING_LIMITS.maxZoom
}

async function responseJson<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as (T & { message?: string }) | null
  if (!response.ok || !payload) throw new Error(payload?.message || 'The request could not be completed.')
  return payload
}

export default function AdminContentMediaPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [snapshot, setSnapshot] = useState<ContentMediaSnapshot | null>(null)
  const [activeTab, setActiveTab] = useState<ContentTab>('homepage')
  const [homepageDraft, setHomepageDraft] = useState<Required<HomepageFields>>(HOMEPAGE_DEFAULTS)
  const [aboutEdit, setAboutEdit] = useState<AboutPresentation>({ stuffedDesktopScale: 1.2, stuffedDesktopOffsetY: -160 })
  const [assignmentEdits, setAssignmentEdits] = useState<Record<string, AssignmentEdit>>({})
  const [productNameEdits, setProductNameEdits] = useState<Record<string, string>>({})
  const [productDescriptionEdits, setProductDescriptionEdits] = useState<Record<string, string>>({})
  const [productWhatsInsideEdits, setProductWhatsInsideEdits] = useState<Record<string, string>>({})
  const [productWeightEdits, setProductWeightEdits] = useState<Record<string, string>>({})
  const [productSafetyEdits, setProductSafetyEdits] = useState<Record<string, ProductSafetyEdit>>({})
  const [productFamilyEdits, setProductFamilyEdits] = useState<Record<string, ProductFamily>>({})
  const [safetyReviewProductId, setSafetyReviewProductId] = useState<string | null>(null)
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({})
  const [search, setSearch] = useState('')
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all')
  const [loading, setLoading] = useState(true)
  const [busyKey, setBusyKey] = useState('')
  const [error, setError] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadTargetProductId, setUploadTargetProductId] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [previewMedia, setPreviewMedia] = useState<MediaItem | null>(null)
  const [archiveMedia, setArchiveMedia] = useState<MediaItem | null>(null)
  const [deleteMedia, setDeleteMedia] = useState<MediaItem | null>(null)
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  function toast(title: string, message: string, tone: AdminToast['tone'] = 'success') {
    const id = crypto.randomUUID()
    setToasts((current) => [...current, { id, title, message, tone }])
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 5000)
  }

  function applySnapshot(next: ContentMediaSnapshot) {
    setSnapshot(next)
    setHomepageDraft(resolveHomepage(next.homepage.draft))
    setAboutEdit(next.about.presentation)
    const edits: Record<string, AssignmentEdit> = {}
    const names: Record<string, string> = {}
    const descriptions: Record<string, string> = {}
    const whatsInside: Record<string, string> = {}
    const weights: Record<string, string> = {}
    const safetyEdits: Record<string, ProductSafetyEdit> = {}
    const families: Record<string, ProductFamily> = {}
    next.products.forEach((product) => {
      edits[product.id] = assignmentDraft(product)
      names[product.id] = product.name
      descriptions[product.id] = product.description
      whatsInside[product.id] = product.whatsInside.join('\n')
      weights[product.id] = product.approxWeightG === null ? '' : String(product.approxWeightG)
      safetyEdits[product.id] = safetyEditFromProduct(product)
      families[product.id] = product.productFamily
    })
    setAssignmentEdits(edits)
    setProductNameEdits(names)
    setProductDescriptionEdits(descriptions)
    setProductWhatsInsideEdits(whatsInside)
    setProductWeightEdits(weights)
    setProductSafetyEdits(safetyEdits)
    setProductFamilyEdits(families)
  }

  async function fetchSnapshot() {
    const response = await fetch('/api/admin/content-media', { credentials: 'same-origin', cache: 'no-store' })
    if (response.status === 401) {
      window.location.replace('/admin/login?next=/admin/content-media')
      return null
    }
    return responseJson<ContentMediaSnapshot>(response)
  }

  async function load() {
    setLoading(true)
    setError('')
    try {
      const sessionResponse = await fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' })
      if (sessionResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/content-media')
        return
      }
      const sessionPayload = await responseJson<SessionPayload>(sessionResponse)
      const contentSnapshot = await fetchSnapshot()
      if (!contentSnapshot) return
      setSession(sessionPayload)
      applySnapshot(contentSnapshot)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Content & Media could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  useEffect(() => () => {
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
  }, [uploadPreviewUrl])

  async function refreshSnapshot() {
    const next = await fetchSnapshot()
    if (next) applySnapshot(next)
    return next
  }

  async function writeJson<T>(path: string, method: 'PATCH' | 'POST', body: unknown) {
    if (!session) throw new Error('Boss Zone session is unavailable.')
    const response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': session.csrfToken,
      },
      body: JSON.stringify(body),
    })
    if (response.status === 401) {
      window.location.replace('/admin/login?next=/admin/content-media')
      throw new Error('Boss Zone session expired.')
    }
    return responseJson<T>(response)
  }

  async function saveHomepageDraft() {
    if (!snapshot || busyKey) return
    setBusyKey('homepage-draft')
    setError('')
    try {
      await writeJson('/api/admin/content-media/homepage-draft', 'PATCH', {
        baseVersion: snapshot.manifestVersion,
        fields: homepageDraft,
      })
      await refreshSnapshot()
      toast('Homepage draft saved', 'Preview the draft before publishing it.')
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Homepage draft could not be saved.')
    } finally {
      setBusyKey('')
    }
  }

  async function publishHomepage() {
    if (!snapshot || busyKey) return
    setBusyKey('homepage-publish')
    setError('')
    try {
      await writeJson('/api/admin/content-media/homepage-publish', 'POST', { baseVersion: snapshot.manifestVersion })
      await refreshSnapshot()
      toast('Homepage content published', 'The customer homepage now uses the published copy.')
    } catch (publishError) {
      setError(publishError instanceof Error ? publishError.message : 'Homepage content could not be published.')
    } finally {
      setBusyKey('')
    }
  }

  function openDraftPreview() {
    window.open('/admin/content-media/preview', '_blank', 'noopener,noreferrer')
  }

  function openOrderDraftPreview(productId?: string) {
    const productQuery = productId ? `&product=${encodeURIComponent(productId)}` : ''
    window.open(`/admin/content-media/preview?surface=order${productQuery}`, '_blank', 'noopener,noreferrer')
  }

  function resetUploadState() {
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
    setUploadPreviewUrl('')
    setUploadFile(null)
    setUploadError('')
    setUploadOpen(false)
    setUploadTargetProductId(null)
  }

  function openUpload(productId: string | null = null) {
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
    setUploadFile(null)
    setUploadPreviewUrl('')
    setUploadError('')
    setUploadTargetProductId(productId)
    setUploadOpen(true)
  }

  function closeUpload() {
    if (busyKey === 'upload') return
    resetUploadState()
  }

  function chooseUploadFile(file: File | null) {
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
    setUploadPreviewUrl('')
    setUploadError('')
    setUploadFile(null)
    if (!file || !snapshot) return
    if (!snapshot.limits.allowedMimeTypes.includes(file.type)) {
      setUploadError('Use a PNG, JPEG or WebP image.')
      return
    }
    if (file.size > snapshot.limits.maxBytes) {
      setUploadError(`This file is too large. Maximum size is ${bytesLabel(snapshot.limits.maxBytes)}.`)
      return
    }
    setUploadFile(file)
    setUploadPreviewUrl(URL.createObjectURL(file))
  }

  async function uploadSelectedFile() {
    if (!snapshot || !session || !uploadFile || busyKey) return
    const uploadedFilename = uploadFile.name
    setBusyKey('upload')
    setUploadError('')
    setError('')
    try {
      const form = new FormData()
      form.append('baseVersion', String(snapshot.manifestVersion))
      form.append('file', uploadFile)
      const response = await fetch('/api/admin/content-media/upload', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
        body: form,
      })
      const uploaded = await responseJson<{ manifestVersion: number; media: MediaItem }>(response)

      if (uploadTargetProductId) {
        const product = snapshot.products.find((item) => item.id === uploadTargetProductId)
        if (!product) throw new Error('The product is no longer available.')
        const edit = assignmentEdits[uploadTargetProductId] ?? assignmentDraft(product)
        const altText = edit.altText.trim() || product.name
        await writeJson(`/api/admin/content-media/products/${encodeURIComponent(uploadTargetProductId)}/draft`, 'POST', {
          baseVersion: uploaded.manifestVersion,
          mediaId: uploaded.media.id,
          altText,
          framing: { ...DEFAULT_PRODUCT_IMAGE_FRAMING },
          hoverBackground: edit.hoverBackground,
        })
        toast('Replacement prepared', `${product.name} now has a new draft image. Preview and publish it when ready.`)
      } else {
        toast('Media uploaded', `${uploadedFilename} is available in the managed media library.`)
      }
      resetUploadState()
      await refreshSnapshot()
    } catch (uploadFailure) {
      setUploadError(uploadFailure instanceof Error ? uploadFailure.message : 'The image could not be uploaded.')
    } finally {
      setBusyKey('')
    }
  }

  async function changeMediaStatus(media: MediaItem, status: 'archived' | 'available') {
    if (!snapshot || busyKey) return
    setBusyKey(`media-${media.id}`)
    setError('')
    try {
      await writeJson(`/api/admin/content-media/media/${encodeURIComponent(media.id)}/${status === 'archived' ? 'archive' : 'restore'}`, 'POST', {
        baseVersion: snapshot.manifestVersion,
      })
      setArchiveMedia(null)
      await refreshSnapshot()
      toast(status === 'archived' ? 'Media archived' : 'Media restored', media.originalFilename)
    } catch (statusError) {
      setError(statusError instanceof Error ? statusError.message : 'The media status could not be changed.')
    } finally {
      setBusyKey('')
    }
  }

  async function deleteMediaPermanently(media: MediaItem) {
    if (!snapshot || busyKey) return
    setBusyKey(`media-delete-${media.id}`)
    setError('')
    try {
      await writeJson(`/api/admin/content-media/media/${encodeURIComponent(media.id)}/delete`, 'POST', {
        baseVersion: snapshot.manifestVersion,
      })
      setDeleteMedia(null)
      await refreshSnapshot()
      toast('Media deleted permanently', media.originalFilename)
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'The media could not be deleted.')
    } finally {
      setBusyKey('')
    }
  }

  function productDetailValues(product: ProductRecord) {
    const whatsInsideText = productWhatsInsideEdits[product.id] ?? product.whatsInside.join('\n')
    const weightText = productWeightEdits[product.id] ?? (product.approxWeightG === null ? '' : String(product.approxWeightG))

    const whatsInside = normalizeWhatsInside(whatsInsideText.split('\n').map((item) => item.trim()).filter(Boolean))
    if (whatsInside === null) {
      return { error: `What's inside supports up to ${FLAVOUR_DETAIL_LIMITS.whatsInsideItems} items of ${FLAVOUR_DETAIL_LIMITS.whatsInsideItemLength} characters each.` } as const
    }

    const parsedWeight = weightText.trim() === '' ? null : Number(weightText)
    const approxWeightG = normalizeApproxWeightG(parsedWeight)
    if (approxWeightG === undefined) {
      return { error: `Approximate weight must be a whole number from ${FLAVOUR_DETAIL_LIMITS.minWeightG}–${FLAVOUR_DETAIL_LIMITS.maxWeightG} g, or left blank.` } as const
    }

    return { error: null, whatsInside, approxWeightG } as const
  }

  async function persistProductDetails(
    product: ProductRecord,
    name: string,
    description: string,
    productFamily: ProductFamily,
    detail: {
      whatsInside: string[]
      approxWeightG: number | null
    },
  ) {
    return writeJson<{
      saved: true
      changed: boolean
      product: {
        id: string
        name: string
        description: string
        productFamily: ProductFamily
        whatsInside: string[]
        approxWeightG: number | null
        safetyReviewStatus?: SafetyReviewStatus
      }
    }>(
      `/api/admin/content-media/products/${encodeURIComponent(product.id)}/catalogue`,
      'PATCH',
      {
        name,
        description,
        productFamily,
        whatsInside: detail.whatsInside,
        approxWeightG: detail.approxWeightG,
      },
    )
  }

  async function persistFlavourDetails(
    product: ProductRecord,
    detail: {
      whatsInside: string[]
      approxWeightG: number | null
    },
  ) {
    return writeJson<{
      saved: true
      changed: boolean
      product: {
        id: string
        whatsInside: string[]
        approxWeightG: number | null
      }
    }>(
      `/api/admin/content-media/products/${encodeURIComponent(product.id)}/flavour-detail`,
      'PATCH',
      {
        whatsInside: detail.whatsInside,
        approxWeightG: detail.approxWeightG,
      },
    )
  }

  async function saveProductDetails(product: ProductRecord) {
    if (!snapshot || busyKey) return
    const rawName = productNameEdits[product.id] ?? product.name
    const rawDescription = productDescriptionEdits[product.id] ?? product.description
    const name = normalizeProductName(rawName)
    const description = normalizeProductDescription(rawDescription)
    const productFamily = productFamilyEdits[product.id] ?? product.productFamily
    const detail = productDetailValues(product)
    if (detail.error) {
      setError(detail.error)
      return
    }

    const detailDirty = JSON.stringify(detail.whatsInside) !== JSON.stringify(product.whatsInside)
      || detail.approxWeightG !== product.approxWeightG
    const catalogueIdentityDirty = rawName !== product.name
      || rawDescription !== product.description
      || productFamily !== product.productFamily
    const catalogueIdentityValid = Boolean(name && description)

    if (!detailDirty && !catalogueIdentityDirty) return
    if (!detailDirty && !catalogueIdentityValid) {
      setError(!name
        ? `Product name must be between 1 and ${PRODUCT_NAME_MAX_LENGTH} characters.`
        : `Product description must be between 1 and ${PRODUCT_DESCRIPTION_MAX_LENGTH} characters.`)
      return
    }

    setBusyKey(`product-catalogue-${product.id}`)
    setError('')
    try {
      let savedDetail = detail
      if (detailDirty) {
        const detailResult = await persistFlavourDetails(product, detail)
        savedDetail = {
          error: null,
          whatsInside: detailResult.product.whatsInside,
          approxWeightG: detailResult.product.approxWeightG,
        }
        setSnapshot((current) => current ? {
          ...current,
          products: current.products.map((item) => item.id === product.id
            ? {
              ...item,
              whatsInside: detailResult.product.whatsInside,
              approxWeightG: detailResult.product.approxWeightG,
            }
            : item),
        } : current)
        setProductWhatsInsideEdits((current) => ({ ...current, [product.id]: detailResult.product.whatsInside.join('\n') }))
        setProductWeightEdits((current) => ({ ...current, [product.id]: detailResult.product.approxWeightG === null ? '' : String(detailResult.product.approxWeightG) }))
      }

      if (catalogueIdentityDirty && catalogueIdentityValid && name && description) {
        const result = await persistProductDetails(product, name, description, productFamily, savedDetail)
        setSnapshot((current) => current ? {
          ...current,
          products: current.products.map((item) => item.id === product.id
            ? {
              ...item,
              name: result.product.name,
              description: result.product.description,
              whatsInside: result.product.whatsInside,
              approxWeightG: result.product.approxWeightG,
              productFamily: result.product.productFamily,
              defaultPresentation: defaultProductPresentation(item.id, result.product.productFamily),
              defaultOrderPresentation: defaultOrderProductFraming(result.product.productFamily),
              safety: result.product.safetyReviewStatus && item.safety
                ? { ...item.safety, status: result.product.safetyReviewStatus }
                : item.safety,
            }
            : item),
        } : current)
        setProductNameEdits((current) => ({ ...current, [product.id]: result.product.name }))
        setProductDescriptionEdits((current) => ({ ...current, [product.id]: result.product.description }))
        setProductWhatsInsideEdits((current) => ({ ...current, [product.id]: result.product.whatsInside.join('\n') }))
        setProductWeightEdits((current) => ({ ...current, [product.id]: result.product.approxWeightG === null ? '' : String(result.product.approxWeightG) }))
        setProductFamilyEdits((current) => ({ ...current, [product.id]: result.product.productFamily }))
      } else if (catalogueIdentityDirty && !catalogueIdentityValid) {
        setError(!name
          ? 'Flavour Detail saved. Product name is still invalid and was not changed.'
          : 'Flavour Detail saved. Product description is still invalid and was not changed.')
      }

      toast(
        'Product details saved',
        detailDirty
          ? `${product.name} Flavour Detail information is saved on the same internal product ID.`
          : `${product.name} catalogue information is saved on the same internal product ID.`,
      )
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Product details could not be saved.')
    } finally {
      setBusyKey('')
    }
  }

  function updateSafetyEdit(product: ProductRecord, patch: Partial<ProductSafetyEdit>) {
    setProductSafetyEdits((current) => ({
      ...current,
      [product.id]: {
        ...(current[product.id] ?? safetyEditFromProduct(product)),
        ...patch,
      },
    }))
  }

  function toggleSafetyAllergen(
    product: ProductRecord,
    field: 'contains' | 'mayContain',
    allergen: ControlledAllergen,
  ) {
    const edit = productSafetyEdits[product.id] ?? safetyEditFromProduct(product)
    const currentValues = edit[field]
    updateSafetyEdit(product, {
      [field]: currentValues.includes(allergen)
        ? currentValues.filter((item) => item !== allergen)
        : [...currentValues, allergen],
    })
  }

  async function saveSafetyDraft(product: ProductRecord) {
    if (busyKey) return
    const edit = productSafetyEdits[product.id] ?? safetyEditFromProduct(product)
    setBusyKey(`product-safety-draft-${product.id}`)
    setError('')
    try {
      await writeJson(
        `/api/admin/content-media/products/${encodeURIComponent(product.id)}/safety-draft`,
        'POST',
        edit,
      )
      await refreshSnapshot()
      toast('Safety draft saved', `${product.name} public safety information is unchanged until Review & Apply.`)
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Product Safety draft could not be saved.')
    } finally {
      setBusyKey('')
    }
  }

  async function applySafetyReview(product: ProductRecord) {
    if (busyKey || !product.safety?.draft) return
    if (!product.safety.draft.sourceReference.trim()) {
      setError('Add the recipe / source reference used for this safety review before applying it.')
      return
    }
    setBusyKey(`product-safety-review-${product.id}`)
    setError('')
    try {
      await writeJson(
        `/api/admin/content-media/products/${encodeURIComponent(product.id)}/safety-review`,
        'POST',
        { expectedDraftUpdatedAt: product.safety.draft.updatedAt },
      )
      setSafetyReviewProductId(null)
      await refreshSnapshot()
      toast('Product Safety reviewed', `${product.name} now uses the reviewed customer-facing safety information.`)
    } catch (reviewError) {
      setError(reviewError instanceof Error ? reviewError.message : 'Product Safety could not be applied.')
    } finally {
      setBusyKey('')
    }
  }

  function updateAssignmentEdit(productId: string, patch: Partial<AssignmentEdit>) {
    setAssignmentEdits((current) => {
      const product = snapshot?.products.find((item) => item.id === productId)
      const fallback = product ? assignmentDraft(product) : {
        mediaId: '',
        altText: '',
        gallery: [],
        framing: { ...DEFAULT_PRODUCT_IMAGE_FRAMING },
        hoverBackground: '#F7EDE2',
      }
      return { ...current, [productId]: { ...(current[productId] ?? fallback), ...patch } }
    })
  }

  function addGalleryImage(product: ProductRecord, mediaId: string) {
    if (!mediaId) return
    const edit = assignmentEdits[product.id] ?? assignmentDraft(product)
    if (edit.gallery.length >= FLAVOUR_DETAIL_LIMITS.galleryImages - 1) return
    if (edit.mediaId === mediaId || edit.gallery.some((item) => item.mediaId === mediaId)) return
    updateAssignmentEdit(product.id, {
      gallery: [...edit.gallery, {
        mediaId,
        altText: `${product.name} detail image`,
        framing: { ...DEFAULT_PRODUCT_IMAGE_FRAMING },
      }],
    })
  }

  function updateGalleryItem(product: ProductRecord, index: number, patch: Partial<ProductGalleryItem>) {
    const edit = assignmentEdits[product.id] ?? assignmentDraft(product)
    const gallery = edit.gallery.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item)
    updateAssignmentEdit(product.id, { gallery })
  }

  function removeGalleryItem(product: ProductRecord, index: number) {
    const edit = assignmentEdits[product.id] ?? assignmentDraft(product)
    updateAssignmentEdit(product.id, { gallery: edit.gallery.filter((_, itemIndex) => itemIndex !== index) })
  }

  function moveGalleryItem(product: ProductRecord, index: number, direction: -1 | 1) {
    const edit = assignmentEdits[product.id] ?? assignmentDraft(product)
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= edit.gallery.length) return
    const gallery = [...edit.gallery]
    ;[gallery[index], gallery[nextIndex]] = [gallery[nextIndex], gallery[index]]
    updateAssignmentEdit(product.id, { gallery })
  }

  function resetHoverColour(product: ProductRecord) {
    updateAssignmentEdit(product.id, { hoverBackground: product.defaultPresentation.hoverBackground })
  }

  function resetPresentation(product: ProductRecord) {
    updateAssignmentEdit(product.id, {
      framing: { ...DEFAULT_PRODUCT_IMAGE_FRAMING },
      hoverBackground: product.defaultPresentation.hoverBackground,
    })
  }

  async function saveAboutPresentation() {
    if (!snapshot || busyKey) return
    setBusyKey('about-save')
    setError('')
    try {
      await writeJson('/api/admin/content-media/about', 'PATCH', {
        baseVersion: snapshot.manifestVersion,
        presentation: aboutEdit,
      })
      await refreshSnapshot()
      window.localStorage.setItem('sg-about-presentation-updated', String(Date.now()))
      toast('About page updated', 'Desktop stuffed-cookie size and position are now live.')
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'About page framing could not be saved.')
    } finally {
      setBusyKey('')
    }
  }

  async function saveProductDraft(product: ProductRecord) {
    if (!snapshot || busyKey) return
    const edit = assignmentEdits[product.id] ?? assignmentDraft(product)
    const rawName = productNameEdits[product.id] ?? product.name
    const rawDescription = productDescriptionEdits[product.id] ?? product.description
    const name = normalizeProductName(rawName)
    const description = normalizeProductDescription(rawDescription)
    const productFamily = productFamilyEdits[product.id] ?? product.productFamily
    const detail = productDetailValues(product)
    if (!name) {
      setError(`Product name must be between 1 and ${PRODUCT_NAME_MAX_LENGTH} characters.`)
      return
    }
    if (!description) {
      setError(`Product description must be between 1 and ${PRODUCT_DESCRIPTION_MAX_LENGTH} characters.`)
      return
    }
    if (detail.error) {
      setError(detail.error)
      return
    }

    const galleryIds = edit.gallery.map((item) => item.mediaId)
    const galleryValid = edit.gallery.length <= FLAVOUR_DETAIL_LIMITS.galleryImages - 1
      && galleryIds.every(Boolean)
      && new Set(galleryIds).size === galleryIds.length
      && !galleryIds.includes(edit.mediaId)
      && edit.gallery.every((item) => (
        item.altText.trim().length > 0
        && item.altText.trim().length <= 220
        && validImageFraming(item.framing)
      ))
    const primaryFramingValid = validImageFraming(edit.framing)
    if (!galleryValid || !primaryFramingValid) {
      setError('Product and gallery images must use valid focal-point framing and complete alt text.')
      return
    }

    const detailDirty = JSON.stringify(detail.whatsInside) !== JSON.stringify(product.whatsInside)
      || detail.approxWeightG !== product.approxWeightG
    const catalogueDirty = name !== product.name
      || description !== product.description
      || productFamily !== product.productFamily
      || detailDirty
    const presentationDirty = !sameEdit(edit, savedDraft(product))
    if (!catalogueDirty && !presentationDirty) return

    setBusyKey(`product-draft-${product.id}`)
    setError('')
    let detailsSaved = false
    try {
      if (catalogueDirty) {
        await persistProductDetails(product, name, description, productFamily, detail)
        detailsSaved = true
      }

      if (presentationDirty) {
        await writeJson(`/api/admin/content-media/products/${encodeURIComponent(product.id)}/draft`, 'POST', {
          baseVersion: snapshot.manifestVersion,
          mediaId: edit.mediaId || null,
          altText: edit.mediaId ? edit.altText : '',
          gallery: edit.gallery.map((item) => ({
            mediaId: item.mediaId,
            altText: item.altText.trim(),
            framing: { ...item.framing },
          })),
          framing: { ...edit.framing },
          hoverBackground: edit.hoverBackground,
        })
      }

      await refreshSnapshot()
      toast(
        'Product card saved',
        presentationDirty
          ? `${name} details, image, gallery and presentation are saved and ready to preview.`
          : `${name} product details are saved.`,
      )
    } catch (saveError) {
      if (detailsSaved) {
        await refreshSnapshot().catch(() => null)
        const message = saveError instanceof Error ? saveError.message : 'The image and presentation draft could not be saved.'
        setError(`Product details were saved, but the remaining draft changes failed: ${message}`)
      } else {
        setError(saveError instanceof Error ? saveError.message : 'The product card could not be saved.')
      }
    } finally {
      setBusyKey('')
    }
  }

  async function publishProduct(product: ProductRecord) {
    if (!snapshot || busyKey) return
    setBusyKey(`product-publish-${product.id}`)
    setError('')
    try {
      await writeJson(`/api/admin/content-media/products/${encodeURIComponent(product.id)}/publish`, 'POST', {
        baseVersion: snapshot.manifestVersion,
      })
      await refreshSnapshot()
      toast('Product presentation published', `${product.name} now uses the complete published image presentation.`)
    } catch (publishError) {
      setError(publishError instanceof Error ? publishError.message : 'The product presentation could not be published.')
    } finally {
      setBusyKey('')
    }
  }

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  const filteredMedia = useMemo(() => {
    if (!snapshot) return []
    const needle = search.trim().toLowerCase()
    return snapshot.media.filter((item) => {
      if (mediaFilter !== 'all' && item.status !== mediaFilter) return false
      if (!needle) return true
      return item.originalFilename.toLowerCase().includes(needle)
        || item.mimeType.toLowerCase().includes(needle)
        || `${item.width}x${item.height}`.includes(needle)
    })
  }, [snapshot, search, mediaFilter])

  if (error && !session && !snapshot && !loading) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel
          state="error"
          title="Content & Media unavailable"
          description={error}
          action={<AdminButton type="button" onClick={() => void load()}>Retry</AdminButton>}
        />
      </main>
    )
  }

  if (!session || !snapshot) {
    return (
      <main className="admin-shell-gate-loading" aria-label="Loading Boss Zone Content and Media">
        <AdminCard><AdminSkeleton lines={8} /></AdminCard>
      </main>
    )
  }

  const homepageServerDraft = resolveHomepage(snapshot.homepage.draft)
  const homepageLocalDirty = JSON.stringify(homepageDraft) !== JSON.stringify(homepageServerDraft)
  const aboutLocalDirty = aboutEdit.stuffedDesktopScale !== snapshot.about.presentation.stuffedDesktopScale
    || aboutEdit.stuffedDesktopOffsetY !== snapshot.about.presentation.stuffedDesktopOffsetY
  const availableMedia = snapshot.media.filter((item) => item.status === 'available')
  const uploadTarget = uploadTargetProductId ? snapshot.products.find((item) => item.id === uploadTargetProductId) ?? null : null
  const safetyReviewProduct = safetyReviewProductId
    ? snapshot.products.find((item) => item.id === safetyReviewProductId) ?? null
    : null

  return (
    <AdminShell
      title="Content & Media"
      eyebrow="CONTENT & COMMS"
      description="Manage approved customer-facing copy and product media without exposing website structure or source files."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton type="button" disabled={!snapshot.storageConfigured} onClick={() => openUpload()}>Upload media</AdminButton>}
    >
      {error && <div className="admin-content-media__inline-error" role="alert">{error}</div>}
      {!snapshot.storageConfigured && (
        <AdminStatePanel
          state="error"
          title="Media storage needs configuration"
          description="Content & Media is safely read-only until managed media storage is configured for this environment. Existing public images and content remain unchanged."
        />
      )}

      <div className="admin-content-media__safety-strip" role="note">
        <strong>Owner-safe controls only</strong>
        <span>Approved product media, framing metadata and structured copy can be managed here. Layout, code, structural artwork, routes, fonts, secrets and infrastructure remain Development-only.</span>
      </div>

      <AdminTabs
        tabs={[
          { id: 'homepage', label: 'Homepage' },
          { id: 'about', label: 'About Page' },
          { id: 'media', label: 'Media Library', badge: snapshot.media.length },
          { id: 'products', label: 'Product Images', badge: snapshot.products.length },
        ]}
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as ContentTab)}
        label="Content and media views"
        idBase="content-media"
      />

      <AdminTabPanel idBase="content-media" tabId="homepage" active={activeTab === 'homepage'}>
        <div className="admin-content-media__status-row">
          <AdminStatusPill tone={snapshot.homepage.dirty ? 'warning' : 'success'}>
            {snapshot.homepage.dirty ? 'Draft differs from live' : 'Draft matches live'}
          </AdminStatusPill>
          <span>Last published: {timestampLabel(snapshot.homepage.publishedAt)}</span>
        </div>
        <div className="admin-content-media__content-layout">
          <AdminCard>
            <AdminCardHeader title="Homepage messaging" description="Structured copy only. The approved Homepage layout, typography and artwork stay locked." />
            <div className="admin-content-media__form-stack">
              <AdminField label="Hero heading · line 1" hint={`${homepageDraft.heroHeadingLineOne.length}/40 characters`} required>
                <AdminInput value={homepageDraft.heroHeadingLineOne} maxLength={40} onChange={(event) => setHomepageDraft((current) => ({ ...current, heroHeadingLineOne: event.target.value }))} />
              </AdminField>
              <AdminField label="Hero heading · line 2" hint={`${homepageDraft.heroHeadingLineTwo.length}/64 characters`} required>
                <AdminInput value={homepageDraft.heroHeadingLineTwo} maxLength={64} onChange={(event) => setHomepageDraft((current) => ({ ...current, heroHeadingLineTwo: event.target.value }))} />
              </AdminField>
              <AdminField label="Hero supporting copy" hint={`${homepageDraft.heroSupportingCopy.length}/180 characters`} required>
                <textarea className="admin-ui-input admin-content-media__textarea" value={homepageDraft.heroSupportingCopy} maxLength={180} rows={4} onChange={(event) => setHomepageDraft((current) => ({ ...current, heroSupportingCopy: event.target.value }))} />
              </AdminField>
              <AdminField label="Weekly menu supporting copy" hint={`${homepageDraft.weeklyMenuSupportingCopy.length}/160 characters`} required>
                <textarea className="admin-ui-input admin-content-media__textarea" value={homepageDraft.weeklyMenuSupportingCopy} maxLength={160} rows={3} onChange={(event) => setHomepageDraft((current) => ({ ...current, weeklyMenuSupportingCopy: event.target.value }))} />
              </AdminField>
            </div>
            <div className="admin-content-media__form-actions">
              <AdminButton variant="ghost" type="button" disabled={busyKey !== ''} onClick={() => setHomepageDraft(HOMEPAGE_DEFAULTS)}>Restore approved copy</AdminButton>
              <AdminButton variant="secondary" type="button" disabled={!snapshot.storageConfigured || busyKey !== '' || homepageLocalDirty} onClick={openDraftPreview}>Preview saved draft</AdminButton>
              <AdminButton type="button" disabled={!snapshot.storageConfigured || busyKey !== '' || !homepageLocalDirty} onClick={() => void saveHomepageDraft()}>{busyKey === 'homepage-draft' ? 'Saving…' : 'Save draft'}</AdminButton>
            </div>
          </AdminCard>

          <AdminCard className="admin-content-media__publish-card">
            <AdminCardHeader title="Publish homepage copy" description="Publishing is deliberate. Unsaved typing never changes the customer site." />
            <div className="admin-content-media__copy-preview" aria-label="Draft homepage content summary">
              <span>{homepageDraft.heroHeadingLineOne}</span>
              <strong>{homepageDraft.heroHeadingLineTwo}</strong>
              <p>{homepageDraft.heroSupportingCopy}</p>
            </div>
            <dl className="admin-content-media__meta-list">
              <div><dt>Draft saved</dt><dd>{timestampLabel(snapshot.homepage.updatedAt)}</dd></div>
              <div><dt>Published</dt><dd>{timestampLabel(snapshot.homepage.publishedAt)}</dd></div>
              <div><dt>State</dt><dd>{snapshot.homepage.dirty ? 'Changes waiting' : 'Up to date'}</dd></div>
            </dl>
            <AdminButton className="admin-content-media__full-button" type="button" disabled={!snapshot.storageConfigured || busyKey !== '' || homepageLocalDirty || !snapshot.homepage.dirty} onClick={() => void publishHomepage()}>
              {busyKey === 'homepage-publish' ? 'Publishing…' : 'Publish saved draft'}
            </AdminButton>
          </AdminCard>
        </div>
      </AdminTabPanel>

      <AdminTabPanel idBase="content-media" tabId="about" active={activeTab === 'about'}>
        <AdminCard>
          <AdminCardHeader
            title="Massive Stuffed Cookie · desktop"
            description="Adjust only the cookie image on the About page at desktop sizes. Tablet and mobile stay unchanged."
          />
          <div className="admin-content-media__form-stack">
            <AdminField label={`Size · ${aboutEdit.stuffedDesktopScale.toFixed(2)}×`} hint="Makes the cookie larger or smaller inside the pink card.">
              <input
                className="admin-content-media__range"
                type="range"
                min={snapshot.about.limits.minScale}
                max={snapshot.about.limits.maxScale}
                step="0.01"
                value={aboutEdit.stuffedDesktopScale}
                disabled={busyKey !== ''}
                onChange={(event) => setAboutEdit((current) => ({ ...current, stuffedDesktopScale: Number(event.target.value) }))}
              />
            </AdminField>
            <AdminField label={`Vertical position · ${aboutEdit.stuffedDesktopOffsetY}px`} hint="More negative moves it up. Less negative moves it down.">
              <input
                className="admin-content-media__range"
                type="range"
                min={snapshot.about.limits.minOffsetY}
                max={snapshot.about.limits.maxOffsetY}
                step="1"
                value={aboutEdit.stuffedDesktopOffsetY}
                disabled={busyKey !== ''}
                onChange={(event) => setAboutEdit((current) => ({ ...current, stuffedDesktopOffsetY: Number(event.target.value) }))}
              />
            </AdminField>
          </div>
          <div className="admin-content-media__form-actions">
            <AdminButton
              variant="ghost"
              type="button"
              disabled={busyKey !== ''}
              onClick={() => setAboutEdit({ stuffedDesktopScale: 1.2, stuffedDesktopOffsetY: -160 })}
            >
              Reset
            </AdminButton>
            <AdminButton
              variant="secondary"
              type="button"
              disabled={busyKey !== ''}
              onClick={() => window.open('/about', '_blank', 'noopener,noreferrer')}
            >
              Open About page
            </AdminButton>
            <AdminButton
              type="button"
              disabled={!snapshot.storageConfigured || busyKey !== '' || !aboutLocalDirty}
              onClick={() => void saveAboutPresentation()}
            >
              {busyKey === 'about-save' ? 'Saving…' : 'Save to site'}
            </AdminButton>
          </div>
          <p className="admin-content-media__helper">
            Saved values apply immediately to the public About page. Refresh the About tab after saving to see the change.
          </p>
        </AdminCard>
      </AdminTabPanel>

      <AdminTabPanel idBase="content-media" tabId="media" active={activeTab === 'media'}>
        <AdminFilterBar actions={<AdminButton type="button" disabled={!snapshot.storageConfigured} onClick={() => openUpload()}>Upload media</AdminButton>}>
          <AdminField label="Search media"><AdminInput type="search" value={search} placeholder="Filename or image type" onChange={(event) => setSearch(event.target.value)} /></AdminField>
          <AdminField label="Status">
            <AdminSelect value={mediaFilter} onChange={(event) => setMediaFilter(event.target.value as MediaFilter)}>
              <option value="all">All media</option><option value="available">Available</option><option value="archived">Archived</option>
            </AdminSelect>
          </AdminField>
        </AdminFilterBar>

        {!snapshot.storageConfigured ? (
          <AdminStatePanel state="error" title="Media storage unavailable" description="Managed media storage must be configured for this environment before uploads or managed media can be used." />
        ) : snapshot.media.length === 0 ? (
          <AdminStatePanel state="empty" title="No managed media yet" description="Approved repository product images remain the safe fallback. Upload an approved business image when you need to prepare a managed replacement." action={<AdminButton type="button" onClick={() => openUpload()}>Upload first image</AdminButton>} />
        ) : filteredMedia.length === 0 ? (
          <AdminStatePanel state="empty" title="No media matches" description="Change the search or status filter to see other managed media." />
        ) : (
          <div className="admin-content-media__media-grid">
            {filteredMedia.map((media) => {
              const usageCount = mediaUsageCount(media)
              return (
                <AdminCard className={`admin-content-media__media-card ${media.status === 'archived' ? 'is-archived' : ''}`} padding="none" key={media.id}>
                  <button className="admin-content-media__media-preview-button" type="button" onClick={() => setPreviewMedia(media)} aria-label={`Preview ${media.originalFilename}`}><img src={media.previewUrl} alt="" /></button>
                  <div className="admin-content-media__media-card-body">
                    <div className="admin-content-media__media-title-row"><strong title={media.originalFilename}>{media.originalFilename}</strong><AdminStatusPill tone={media.status === 'available' ? 'success' : 'neutral'}>{media.status === 'available' ? 'Available' : 'Archived'}</AdminStatusPill></div>
                    <p>{media.width} × {media.height}px · {bytesLabel(media.byteSize)}</p>
                    <p>{media.mimeType.replace('image/', '').toUpperCase()} · {usageCount ? `Used by ${usageCount} product${usageCount === 1 ? '' : 's'}` : 'Not assigned'}</p>
                    {usageCount > 0 ? (
                      <p className="admin-content-media__media-usage-warning">
                        In use — remove this image from all product assignments before archiving or deleting it.
                      </p>
                    ) : null}
                    <div className="admin-content-media__media-actions">
                      <AdminButton variant="secondary" compact type="button" onClick={() => setPreviewMedia(media)}>Preview</AdminButton>
                      {media.status === 'available' ? (
                        <AdminButton variant="ghost" compact type="button" disabled={usageCount > 0 || busyKey !== ''} title={usageCount > 0 ? 'Remove product assignments before archiving this image.' : undefined} onClick={() => setArchiveMedia(media)}>Archive</AdminButton>
                      ) : (
                        <>
                          <AdminButton variant="secondary" compact type="button" disabled={busyKey !== ''} onClick={() => void changeMediaStatus(media, 'available')}>Restore</AdminButton>
                          <AdminButton
                            variant="ghost"
                            compact
                            type="button"
                            disabled={usageCount > 0 || busyKey !== ''}
                            title={usageCount > 0 ? 'Remove product assignments before deleting this image.' : undefined}
                            onClick={() => setDeleteMedia(media)}
                          >
                            Delete permanently
                          </AdminButton>
                        </>
                      )}
                    </div>
                  </div>
                </AdminCard>
              )
            })}
          </div>
        )}
      </AdminTabPanel>

      <AdminTabPanel idBase="content-media" tabId="products" active={activeTab === 'products'}>
        <div className="admin-content-media__product-intro">
          <div><strong>Approved product catalogue</strong><span>Products are collapsed by default. Open only the flavour you need; collapsing a card does not discard unsaved edits.</span></div>
          <div className="admin-content-media__product-intro-actions">
            <AdminButton variant="ghost" compact type="button" onClick={() => setExpandedProducts(Object.fromEntries(snapshot.products.map((product) => [product.id, true])))}>Expand all</AdminButton>
            <AdminButton variant="ghost" compact type="button" onClick={() => setExpandedProducts({})}>Collapse all</AdminButton>
            <AdminButton variant="secondary" type="button" disabled={!snapshot.storageConfigured || busyKey !== ''} onClick={openDraftPreview}>Preview all saved drafts</AdminButton>
          </div>
        </div>
        <div className="admin-content-media__product-grid">
          {snapshot.products.map((product) => {
            const edit = assignmentEdits[product.id] ?? assignmentDraft(product)
            const saved = savedDraft(product)
            const publishedFraming = publishedImageFraming(product)
            const draftManagedUrl = managedMediaUrl(edit.mediaId || null, snapshot.media)
            const draftImage = draftManagedUrl ?? product.staticImage
            const nameEdit = productNameEdits[product.id] ?? product.name
            const descriptionEdit = productDescriptionEdits[product.id] ?? product.description
            const whatsInsideEdit = productWhatsInsideEdits[product.id] ?? product.whatsInside.join('\n')
            const weightEdit = productWeightEdits[product.id] ?? (product.approxWeightG === null ? '' : String(product.approxWeightG))
            const safetyEdit = productSafetyEdits[product.id] ?? safetyEditFromProduct(product)
            const savedSafetyEdit = safetyEditFromProduct(product)
            const safetyDraftDirty = !sameSafetyEdit(safetyEdit, savedSafetyEdit)
            const safetyStatus = product.safety?.status ?? 'needs-review'
            const familyEdit = productFamilyEdits[product.id] ?? product.productFamily
            const normalizedName = normalizeProductName(nameEdit)
            const normalizedDescription = normalizeProductDescription(descriptionEdit)
            const detail = productDetailValues(product)
            const detailValid = detail.error === null
            const detailDirty = detailValid && (
              JSON.stringify(detail.whatsInside) !== JSON.stringify(product.whatsInside)
              || detail.approxWeightG !== product.approxWeightG
            )
            const nameDirty = normalizedName !== null && normalizedName !== product.name
            const descriptionDirty = normalizedDescription !== null && normalizedDescription !== product.description
            const familyDirty = familyEdit !== product.productFamily
            const catalogueDirty = nameDirty || descriptionDirty || familyDirty || detailDirty
            const nameValid = normalizedName !== null
            const descriptionValid = normalizedDescription !== null
            const catalogueIdentityDirty = nameEdit !== product.name || descriptionEdit !== product.description || familyDirty
            const productDetailsSaveable = detailDirty || (catalogueIdentityDirty && nameValid && descriptionValid)
            const galleryIds = edit.gallery.map((item) => item.mediaId)
            const galleryValid = edit.gallery.length <= FLAVOUR_DETAIL_LIMITS.galleryImages - 1
              && new Set(galleryIds).size === galleryIds.length
              && !galleryIds.includes(edit.mediaId)
              && edit.gallery.every((item) => (
                Boolean(item.mediaId)
                && item.altText.trim().length > 0
                && item.altText.trim().length <= 220
                && validImageFraming(item.framing)
              ))
            const primaryFramingValid = validImageFraming(edit.framing)
            const localDirty = !sameEdit(edit, saved)
            const cardDirty = catalogueDirty || localDirty
            const selectedManaged = Boolean(edit.mediaId)
            const colourValid = normalizeHexColor(edit.hoverBackground) !== null
            const expanded = Boolean(expandedProducts[product.id])
            return (
              <AdminCard className={`admin-content-media__product-card ${expanded ? 'is-expanded' : 'is-collapsed'}`} key={product.id}>
                <div className="admin-content-media__product-heading">
                  <button
                    className="admin-content-media__product-summary"
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={`content-media-product-${product.id}`}
                    onClick={() => setExpandedProducts((current) => ({ ...current, [product.id]: !expanded }))}
                  >
                    <span>{familyEdit}</span>
                    <h2>{normalizedName ?? product.name}</h2>
                    <small>{expanded ? 'Hide product controls' : 'Open product controls'}</small>
                  </button>
                  <div className="admin-content-media__product-heading-actions">
                    {cardDirty ? <AdminStatusPill tone="warning">Unsaved changes</AdminStatusPill> : null}
                    {safetyDraftDirty ? <AdminStatusPill tone="warning">Unsaved safety draft</AdminStatusPill> : null}
                    <AdminStatusPill tone={safetyStatusTone(safetyStatus)}>{safetyStatusLabel(safetyStatus)}</AdminStatusPill>
                    <AdminStatusPill tone={!product.active ? 'neutral' : product.assignment?.dirty ? 'warning' : 'success'}>{!product.active ? 'Inactive' : product.assignment?.dirty ? 'Draft waiting' : 'Published'}</AdminStatusPill>
                    <AdminButton
                      variant="ghost"
                      compact
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={`content-media-product-${product.id}`}
                      onClick={() => setExpandedProducts((current) => ({ ...current, [product.id]: !expanded }))}
                    >{expanded ? 'Collapse' : 'Open'}</AdminButton>
                  </div>
                </div>

                {expanded ? <div id={`content-media-product-${product.id}`} className="admin-content-media__product-body">
                <div className="admin-content-media__form-stack">
                  <AdminField label="Product / flavour name" hint={`Customer-facing name · 1–${PRODUCT_NAME_MAX_LENGTH} characters. Internal product ID stays fixed.`}>
                    <AdminInput
                      value={nameEdit}
                      maxLength={PRODUCT_NAME_MAX_LENGTH}
                      aria-invalid={!nameValid}
                      disabled={busyKey !== ''}
                      onChange={(event) => setProductNameEdits((current) => ({ ...current, [product.id]: event.target.value }))}
                    />
                    {!nameValid && <span className="admin-content-media__field-error" role="alert">Enter a product name before saving.</span>}
                  </AdminField>
                  <AdminField label="Product description" hint={`${descriptionEdit.length}/${PRODUCT_DESCRIPTION_MAX_LENGTH} characters · customer-facing copy used on menu/product cards.`}>
                    <textarea
                      className="admin-ui-input admin-content-media__textarea admin-content-media__textarea--short"
                      rows={3}
                      maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
                      value={descriptionEdit}
                      aria-invalid={!descriptionValid}
                      disabled={busyKey !== ''}
                      onChange={(event) => setProductDescriptionEdits((current) => ({ ...current, [product.id]: event.target.value }))}
                    />
                    {!descriptionValid && <span className="admin-content-media__field-error" role="alert">Enter a product description before saving.</span>}
                  </AdminField>
                  <AdminField label="Product family" hint="Controlled catalogue family. This does not change the internal product ID or detach media.">
                    <AdminSelect
                      value={familyEdit}
                      disabled={busyKey !== ''}
                      onChange={(event) => setProductFamilyEdits((current) => ({
                        ...current,
                        [product.id]: event.target.value as ProductFamily,
                      }))}
                    >
                      {PRODUCT_FAMILIES.map((family) => <option value={family} key={family}>{family}</option>)}
                    </AdminSelect>
                  </AdminField>
                  <fieldset className="admin-content-media__presentation-section">
                    <legend>Flavour detail</legend>
                    <p>Persistent product information. Leave unknown factual fields blank rather than guessing.</p>
                    <AdminField label="What's inside" hint={`One factual component per line · up to ${FLAVOUR_DETAIL_LIMITS.whatsInsideItems} items.`}>
                      <textarea
                        className="admin-ui-input admin-content-media__textarea"
                        rows={4}
                        value={whatsInsideEdit}
                        disabled={busyKey !== ''}
                        placeholder="Chocolate cookie dough\nRich chocolate centre"
                        onChange={(event) => setProductWhatsInsideEdits((current) => ({ ...current, [product.id]: event.target.value }))}
                      />
                    </AdminField>
                    <AdminField label="Approximate weight (g)" hint="Optional. Leave blank when the product has not been weighed.">
                      <AdminInput
                        type="number"
                        min={FLAVOUR_DETAIL_LIMITS.minWeightG}
                        max={FLAVOUR_DETAIL_LIMITS.maxWeightG}
                        step={1}
                        value={weightEdit}
                        disabled={busyKey !== ''}
                        placeholder="140"
                        onChange={(event) => setProductWeightEdits((current) => ({ ...current, [product.id]: event.target.value }))}
                      />
                    </AdminField>
                    {!detailValid && <span className="admin-content-media__field-error" role="alert">{detail.error}</span>}
                  </fieldset>

                  <fieldset className="admin-content-media__presentation-section admin-content-media__product-safety">
                    <legend>Product safety</legend>
                    <div className="admin-content-media__safety-heading">
                      <div>
                        <strong>Customer safety authority</strong>
                        <span>Draft changes stay unpublished until Review & Apply.</span>
                      </div>
                      <AdminStatusPill tone={safetyStatusTone(safetyStatus)}>{safetyStatusLabel(safetyStatus)}</AdminStatusPill>
                    </div>

                    {product.safety?.reviewedAt && (
                      <p className="admin-content-media__safety-meta">
                        Last reviewed {timestampLabel(product.safety.reviewedAt)}
                      </p>
                    )}

                    {product.safety?.published.legacyUnrecognised.length ? (
                      <div className="admin-content-media__safety-warning" role="alert">
                        <strong>Legacy / unrecognised allergen value</strong>
                        <span>{product.safety.published.legacyUnrecognised.join(', ')}</span>
                        <p>These values remain unchanged publicly. Resolve them in the controlled draft before Review & Apply.</p>
                      </div>
                    ) : null}

                    <div className="admin-content-media__published-safety">
                      <strong>Currently published</strong>
                      <dl>
                        <div><dt>Ingredients</dt><dd>{product.safety?.published.ingredients || 'Not supplied'}</dd></div>
                        <div><dt>Contains</dt><dd>{product.safety?.published.contains.join(', ') || 'None supplied'}</dd></div>
                        <div><dt>May contain</dt><dd>{product.safety?.published.mayContain.join(', ') || 'None supplied'}</dd></div>
                      </dl>
                    </div>

                    <AdminField
                      label="Ingredients"
                      hint={`Safety draft only · up to ${FLAVOUR_DETAIL_LIMITS.ingredientsLength} characters.`}
                    >
                      <textarea
                        className="admin-ui-input admin-content-media__textarea"
                        rows={5}
                        maxLength={FLAVOUR_DETAIL_LIMITS.ingredientsLength}
                        value={safetyEdit.ingredients}
                        disabled={busyKey !== ''}
                        onChange={(event) => updateSafetyEdit(product, { ingredients: event.target.value })}
                      />
                    </AdminField>

                    <div className="admin-content-media__allergen-field">
                      <strong>Contains</strong>
                      <span>Allergens actually present. Choose only reviewed, customer-facing declarations.</span>
                      <div className="admin-content-media__allergen-grid">
                        {CONTROLLED_ALLERGENS.map((allergen) => (
                          <label key={`contains-${product.id}-${allergen}`}>
                            <input
                              type="checkbox"
                              checked={safetyEdit.contains.includes(allergen)}
                              disabled={busyKey !== ''}
                              onChange={() => toggleSafetyAllergen(product, 'contains', allergen)}
                            />
                            <span>{allergen}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="admin-content-media__allergen-field">
                      <strong>May contain</strong>
                      <span>Use only for genuine identified cross-contact risk. Do not duplicate Contains automatically.</span>
                      <div className="admin-content-media__allergen-grid">
                        {CONTROLLED_ALLERGENS.map((allergen) => (
                          <label key={`may-contain-${product.id}-${allergen}`}>
                            <input
                              type="checkbox"
                              checked={safetyEdit.mayContain.includes(allergen)}
                              disabled={busyKey !== ''}
                              onChange={() => toggleSafetyAllergen(product, 'mayContain', allergen)}
                            />
                            <span>{allergen}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <AdminField
                      label="Recipe / source reference"
                      hint="Internal only · required for Review & Apply · optional while saving an incomplete draft."
                      required
                    >
                      <AdminInput
                        value={safetyEdit.sourceReference}
                        maxLength={240}
                        disabled={busyKey !== ''}
                        placeholder="Massive Stuffed Cookie V1 + current supplier labels"
                        onChange={(event) => updateSafetyEdit(product, { sourceReference: event.target.value })}
                      />
                    </AdminField>

                    <div className="admin-content-media__safety-actions">
                      <AdminButton
                        variant="secondary"
                        type="button"
                        disabled={busyKey !== '' || !safetyDraftDirty}
                        onClick={() => void saveSafetyDraft(product)}
                      >
                        {busyKey === `product-safety-draft-${product.id}` ? 'Saving safety draft…' : 'Save safety draft'}
                      </AdminButton>
                      <AdminButton
                        type="button"
                        disabled={
                          busyKey !== ''
                          || safetyDraftDirty
                          || !product.safety?.draft
                          || !product.safety.draft.sourceReference.trim()
                        }
                        onClick={() => setSafetyReviewProductId(product.id)}
                      >
                        Review & Apply
                      </AdminButton>
                    </div>
                    {product.safety?.draft && !safetyDraftDirty && (
                      <p className="admin-content-media__safety-meta">
                        Saved safety draft · {timestampLabel(product.safety.draft.updatedAt)}
                      </p>
                    )}
                    {product.safety?.draft && !safetyDraftDirty && !product.safety.draft.sourceReference.trim() && (
                      <p className="admin-content-media__safety-warning" role="alert">
                        Add a recipe / source reference and save the safety draft before Review & Apply.
                      </p>
                    )}
                  </fieldset>
                </div>

                <fieldset className="admin-content-media__presentation-section">
                  <legend>Primary product image framing</legend>
                  <p>One focal point and zoom is used across Homepage, Order cards and Flavour Detail. Click what matters, adjust zoom, then check all three previews.</p>
                  <ProductImageFramingEditor
                    src={draftImage}
                    alt={normalizedName ?? product.name}
                    framing={edit.framing}
                    disabled={busyKey !== ''}
                    label={`${normalizedName ?? product.name} primary image framing`}
                    onChange={(framing) => updateAssignmentEdit(product.id, { framing })}
                  />
                  <p className="admin-content-media__order-framing-note">
                    Published framing · X {Math.round(publishedFraming.focalX * 100)}% · Y {Math.round(publishedFraming.focalY * 100)}% · {publishedFraming.zoom.toFixed(2)}×
                  </p>
                </fieldset>

                <div className="admin-content-media__form-stack">
                  <AdminField label="Product image" hint="Approved repository image remains available as the safe fallback.">
                    <AdminSelect
                      value={edit.mediaId}
                      disabled={!snapshot.storageConfigured || busyKey !== ''}
                      onChange={(event) => updateAssignmentEdit(product.id, {
                        mediaId: event.target.value,
                        framing: { ...DEFAULT_PRODUCT_IMAGE_FRAMING },
                      })}
                    >
                      <option value="">Approved repository image</option>
                      {availableMedia.map((media) => <option value={media.id} key={media.id}>{media.originalFilename}</option>)}
                    </AdminSelect>
                  </AdminField>
                  <AdminField label="Customer-facing alt text" hint={selectedManaged ? `${edit.altText.length}/220 characters · describe the product image for customers using assistive technology.` : 'The approved fallback image uses the product name.'} required={selectedManaged}>
                    <textarea className="admin-ui-input admin-content-media__textarea admin-content-media__textarea--short" rows={2} maxLength={220} value={selectedManaged ? edit.altText : ''} disabled={!selectedManaged || busyKey !== ''} placeholder={selectedManaged ? `Describe the ${product.name} product image` : product.name} onChange={(event) => updateAssignmentEdit(product.id, { altText: event.target.value })} />
                  </AdminField>

                  <fieldset className="admin-content-media__presentation-section admin-content-media__gallery-section">
                    <legend>Flavour Detail gallery</legend>
                    <p>Optional secondary approved images only. Each image stores the same focal-point + zoom model as the primary image.</p>
                    {edit.gallery.length > 0 ? (
                      <div className="admin-content-media__gallery-list">
                        {edit.gallery.map((galleryItem, galleryIndex) => (
                          <div className="admin-content-media__gallery-item" key={`${galleryItem.mediaId}-${galleryIndex}`}>
                            <div className="admin-content-media__gallery-fields">
                              <AdminField label={`Secondary image ${galleryIndex + 1}`}>
                                <AdminSelect
                                  value={galleryItem.mediaId}
                                  disabled={!snapshot.storageConfigured || busyKey !== ''}
                                  onChange={(event) => updateGalleryItem(product, galleryIndex, {
                                    mediaId: event.target.value,
                                    framing: { ...DEFAULT_PRODUCT_IMAGE_FRAMING },
                                  })}
                                >
                                  {availableMedia
                                    .filter((media) => media.id === galleryItem.mediaId || (
                                      media.id !== edit.mediaId
                                      && !edit.gallery.some((item, index) => index !== galleryIndex && item.mediaId === media.id)
                                    ))
                                    .map((media) => <option value={media.id} key={media.id}>{media.originalFilename}</option>)}
                                </AdminSelect>
                              </AdminField>
                              <AdminField label="Gallery alt text" hint={`${galleryItem.altText.length}/220 characters`} required>
                                <AdminInput
                                  value={galleryItem.altText}
                                  maxLength={220}
                                  disabled={busyKey !== ''}
                                  onChange={(event) => updateGalleryItem(product, galleryIndex, { altText: event.target.value })}
                                />
                              </AdminField>
                            </div>

                            {managedMediaUrl(galleryItem.mediaId, snapshot.media) && (
                              <ProductImageFramingEditor
                                src={managedMediaUrl(galleryItem.mediaId, snapshot.media) ?? ''}
                                alt={galleryItem.altText}
                                framing={galleryItem.framing}
                                disabled={busyKey !== ''}
                                label={`${normalizedName ?? product.name} secondary image ${galleryIndex + 1} framing`}
                                onChange={(framing) => updateGalleryItem(product, galleryIndex, { framing })}
                                primarySurfaces={false}
                              />
                            )}

                            <div className="admin-content-media__gallery-actions">
                              <AdminButton variant="ghost" compact type="button" disabled={busyKey !== '' || galleryIndex === 0} onClick={() => moveGalleryItem(product, galleryIndex, -1)}>Move up</AdminButton>
                              <AdminButton variant="ghost" compact type="button" disabled={busyKey !== '' || galleryIndex === edit.gallery.length - 1} onClick={() => moveGalleryItem(product, galleryIndex, 1)}>Move down</AdminButton>
                              <AdminButton variant="secondary" compact type="button" disabled={busyKey !== ''} onClick={() => removeGalleryItem(product, galleryIndex)}>Remove</AdminButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-content-media__order-framing-note">No secondary gallery images. The Flavour Detail view will use the existing primary product image only.</p>
                    )}
                    {edit.gallery.length < FLAVOUR_DETAIL_LIMITS.galleryImages - 1 && (
                      <AdminField label="Add secondary image" hint={`Optional · up to ${FLAVOUR_DETAIL_LIMITS.galleryImages - 1} secondary images.`}>
                        <AdminSelect
                          value=""
                          disabled={!snapshot.storageConfigured || busyKey !== ''}
                          onChange={(event) => {
                            addGalleryImage(product, event.target.value)
                            event.currentTarget.value = ''
                          }}
                        >
                          <option value="">Choose managed media…</option>
                          {availableMedia
                            .filter((media) => media.id !== edit.mediaId && !edit.gallery.some((item) => item.mediaId === media.id))
                            .map((media) => <option value={media.id} key={media.id}>{media.originalFilename}</option>)}
                        </AdminSelect>
                      </AdminField>
                    )}
                    {!galleryValid && <span className="admin-content-media__field-error" role="alert">Gallery images must be unique, different from the primary image, include alt text, and use valid framing.</span>}
                  </fieldset>

                  <fieldset className="admin-content-media__presentation-section">
                    <legend>Hover background</legend>
                    <p>Exact colour used by the existing customer hover/focus treatment.</p>
                    <div className="admin-content-media__colour-controls">
                      <input
                        className="admin-content-media__colour-picker"
                        type="color"
                        aria-label={`${product.name} hover background colour`}
                        value={normalizeHexColor(edit.hoverBackground) ?? product.defaultPresentation.hoverBackground}
                        disabled={busyKey !== ''}
                        onChange={(event) => updateAssignmentEdit(product.id, { hoverBackground: event.target.value.toUpperCase() })}
                      />
                      <AdminInput
                        value={edit.hoverBackground}
                        maxLength={7}
                        aria-invalid={!colourValid}
                        disabled={busyKey !== ''}
                        onChange={(event) => updateAssignmentEdit(product.id, { hoverBackground: event.target.value.toUpperCase() })}
                      />
                      <span className="admin-content-media__colour-swatch" style={{ background: normalizeHexColor(edit.hoverBackground) ?? 'transparent' }} aria-hidden="true" />
                    </div>
                    {!colourValid && <span className="admin-content-media__field-error" role="alert">Use an exact colour in #RRGGBB format.</span>}
                    <div className="admin-content-media__presentation-buttons">
                      <AdminButton variant="ghost" compact type="button" disabled={busyKey !== ''} onClick={() => resetHoverColour(product)}>Reset colour</AdminButton>
                    </div>
                  </fieldset>

                  <div className="admin-content-media__presentation-reset">
                    <AdminButton variant="ghost" type="button" disabled={busyKey !== ''} onClick={() => resetPresentation(product)}>Reset presentation</AdminButton>
                    <span>Restores neutral focal-point framing and the approved flavour hover colour in local draft state only.</span>
                  </div>
                </div>

                <div className="admin-content-media__product-actions">
                  <AdminButton
                    variant="secondary"
                    type="button"
                    disabled={busyKey !== '' || !detailValid || !productDetailsSaveable}
                    onClick={() => void saveProductDetails(product)}
                  >
                    {busyKey === `product-catalogue-${product.id}` ? 'Saving details…' : 'Save product details'}
                  </AdminButton>
                  <AdminButton variant="secondary" type="button" disabled={!snapshot.storageConfigured || busyKey !== ''} onClick={() => openUpload(product.id)}>Replace with upload</AdminButton>
                  <AdminButton type="button" disabled={!snapshot.storageConfigured || busyKey !== '' || !cardDirty || !nameValid || !descriptionValid || !detailValid || !galleryValid || !primaryFramingValid || !colourValid || (selectedManaged && !edit.altText.trim())} onClick={() => void saveProductDraft(product)}>{busyKey === `product-draft-${product.id}` ? 'Saving…' : 'Save card draft'}</AdminButton>
                  <AdminButton variant="secondary" type="button" disabled={!snapshot.storageConfigured || busyKey !== '' || cardDirty || !product.assignment?.dirty} onClick={() => openOrderDraftPreview(product.id)}>Preview</AdminButton>
                  <AdminButton type="button" disabled={!snapshot.storageConfigured || busyKey !== '' || cardDirty || !product.assignment?.dirty} onClick={() => void publishProduct(product)}>{busyKey === `product-publish-${product.id}` ? 'Publishing…' : 'Publish'}</AdminButton>
                </div>
                </div> : null}
              </AdminCard>
            )
          })}
        </div>
      </AdminTabPanel>

      <AdminModal
        open={Boolean(safetyReviewProduct)}
        title={safetyReviewProduct ? `Review & Apply · ${safetyReviewProduct.name}` : 'Review Product Safety'}
        description="Confirm the exact customer-facing safety information below. Applying it replaces the published Ingredients / Contains / May contain record atomically."
        onClose={() => { if (!busyKey.startsWith('product-safety-review-')) setSafetyReviewProductId(null) }}
        footer={(
          <>
            <AdminButton
              variant="secondary"
              type="button"
              disabled={busyKey.startsWith('product-safety-review-')}
              onClick={() => setSafetyReviewProductId(null)}
            >
              Cancel
            </AdminButton>
            <AdminButton
              type="button"
              disabled={
                !safetyReviewProduct?.safety?.draft
                || !safetyReviewProduct.safety.draft.sourceReference.trim()
                || busyKey.startsWith('product-safety-review-')
              }
              onClick={() => { if (safetyReviewProduct) void applySafetyReview(safetyReviewProduct) }}
            >
              {safetyReviewProduct && busyKey === `product-safety-review-${safetyReviewProduct.id}`
                ? 'Applying reviewed safety…'
                : 'Apply reviewed safety'}
            </AdminButton>
          </>
        )}
      >
        {safetyReviewProduct?.safety?.draft && (
          <div className="admin-content-media__safety-confirm">
            <div>
              <strong>Ingredients</strong>
              <p>{safetyReviewProduct.safety.draft.ingredients || 'Not supplied'}</p>
            </div>
            <div>
              <strong>Contains</strong>
              <p>{safetyReviewProduct.safety.draft.contains.join(', ') || 'None declared'}</p>
            </div>
            <div>
              <strong>May contain</strong>
              <p>{safetyReviewProduct.safety.draft.mayContain.join(', ') || 'None declared'}</p>
            </div>
            <div className="admin-content-media__safety-confirm-source">
              <strong>Recipe / source reference · internal only</strong>
              <p>{safetyReviewProduct.safety.draft.sourceReference || 'Required before review'}</p>
            </div>
          </div>
        )}
      </AdminModal>

      <AdminModal
        open={uploadOpen}
        title={uploadTarget ? `Replace ${uploadTarget.name} image` : 'Upload approved media'}
        description={uploadTarget ? 'The upload becomes a draft replacement only. It will not change the public site until you explicitly publish it.' : 'Upload an approved business image to the managed media library.'}
        onClose={closeUpload}
        footer={<><AdminButton variant="secondary" type="button" disabled={busyKey === 'upload'} onClick={closeUpload}>Cancel</AdminButton><AdminButton type="button" disabled={!uploadFile || busyKey === 'upload'} onClick={() => void uploadSelectedFile()}>{busyKey === 'upload' ? 'Uploading…' : uploadTarget ? 'Upload & prepare draft' : 'Upload media'}</AdminButton></>}
      >
        <div className="admin-content-media__upload-form">
          <AdminField label="Image file" hint={`PNG, JPEG or WebP · max ${bytesLabel(snapshot.limits.maxBytes)} · ${snapshot.limits.minDimension}–${snapshot.limits.maxDimension}px per side`} error={uploadError || undefined} required>
            <input className="admin-content-media__file-input" type="file" accept="image/png,image/jpeg,image/webp" disabled={busyKey === 'upload'} onChange={(event) => chooseUploadFile(event.target.files?.[0] ?? null)} />
          </AdminField>
          {uploadFile && (
            <div className="admin-content-media__chosen-file">
              {uploadPreviewUrl && <img src={uploadPreviewUrl} alt="Preview of selected upload" />}
              <div><strong>{uploadFile.name}</strong><span>{bytesLabel(uploadFile.size)} · {uploadFile.type || 'type will be detected server-side'}</span><small>The original bytes are preserved. Content & Media does not retouch, recolour or regenerate product photography.</small></div>
            </div>
          )}
        </div>
      </AdminModal>

      <AdminModal open={Boolean(previewMedia)} title={previewMedia?.originalFilename ?? 'Media preview'} description={previewMedia ? `${previewMedia.width} × ${previewMedia.height}px · ${bytesLabel(previewMedia.byteSize)}` : undefined} onClose={() => setPreviewMedia(null)} footer={<AdminButton variant="secondary" type="button" onClick={() => setPreviewMedia(null)}>Close</AdminButton>}>
        {previewMedia && <div className="admin-content-media__large-preview"><img src={previewMedia.previewUrl} alt={`Preview of ${previewMedia.originalFilename}`} /><p>This preview shows the stored original. No visual edits are applied by Boss Zone.</p></div>}
      </AdminModal>

      <AdminConfirmDialog open={Boolean(archiveMedia)} title="Archive this media?" description={archiveMedia ? `${archiveMedia.originalFilename} will leave the available media library but its original file will be retained and can be restored later.` : ''} confirmLabel="Archive media" dangerous onConfirm={() => { if (archiveMedia) void changeMediaStatus(archiveMedia, 'archived') }} onCancel={() => setArchiveMedia(null)} />
      <AdminConfirmDialog
        open={Boolean(deleteMedia)}
        title="Delete this media permanently?"
        description={deleteMedia ? `${deleteMedia.originalFilename} will be removed from the media library and its stored original will be deleted. This cannot be undone.` : ''}
        confirmLabel="Delete permanently"
        dangerous
        onConfirm={() => { if (deleteMedia) void deleteMediaPermanently(deleteMedia) }}
        onCancel={() => setDeleteMedia(null)}
      />
      <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((item) => item.id !== id))} />
    </AdminShell>
  )
}
